'use client';

import { useEffect, type ReactNode } from 'react';
import { getPageDir, getPageLang, isRtlLanguage } from '@/lib/rtl';
import { setupRootFontSize } from '@/lib/rootFontSize';

interface PageShellProps {
  children: ReactNode;
  isChatPage?: boolean;
  language?: string;
}

export function PageShell({
  children,
  isChatPage,
  language,
}: PageShellProps) {
  const dir = getPageDir(language);
  const lang = getPageLang(language);
  const rtl = isRtlLanguage(language);

  useEffect(() => {
    setupRootFontSize();
  }, []);

  useEffect(() => {
    if (isChatPage) {
      document.body.classList.add('page-type-chat');
    } else {
      document.body.classList.remove('page-type-chat');
    }
    return () => document.body.classList.remove('page-type-chat');
  }, [isChatPage]);

  useEffect(() => {
    const html = document.documentElement;
    html.setAttribute('dir', dir);
    html.setAttribute('lang', lang);
    if (rtl) {
      document.body.classList.add('page-lang-rtl');
    } else {
      document.body.classList.remove('page-lang-rtl');
    }
    return () => {
      html.removeAttribute('dir');
      html.removeAttribute('lang');
      document.body.classList.remove('page-lang-rtl');
    };
  }, [dir, lang, rtl]);

  return (
    <div
      dir={dir}
      lang={lang}
      className={rtl ? 'page-lang-rtl' : undefined}
      role="presentation"
    >
      {children}
    </div>
  );
}
